///
/// EarthBrowser
///
/// Created by Matt Giger on 10/28/2026
/// Copyright (c) 2026 Geodata Labs
///

import type { EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { wgs } from "@/earthbrowser/utils/geographic"
import { isMobile } from "@/earthbrowser/utils/platform"
import { hapticTick } from "@/native/haptics"
import { GestureState } from "@stovokor/gestures/GestureRecognizer"
import { TwoFingerGestureRecognizer, type TwoFingerGestureEventData } from "@stovokor/gestures/TwoFingerGestureRecognizer"
import { trig } from "@stovokor/math"
import type { CameraParams, EarthCameraState, FulcrumOrbitAnchor, GroundDragAnchor } from "../EarthCameraState"

// Tilt per pixel of vertical two-finger travel (at the reference viewport
// height), as the two-finger swipe always had
const cCameraTiltSensitivity = trig.pi / 1000.0
const cReferenceViewportHeight = 1000
// Pan tracking is tight (30); the pinch was 15. A combined gesture takes one
// spring for all of it, in between
const cTransformSpringDamping = 20
const cTiltSpringDamping = 30

/**
 * EarthTwoFingerHandler - everything two fingers do on the globe.
 *
 * Tilt mode (fingers travelling together, mostly vertically): vertical travel
 * tilts about the fulcrum as the two-finger swipe always did; horizontal
 * travel pans the tilted view (ground-locked), so a diagonal swipe tilts and
 * pans the way phone maps do instead of changing the bearing.
 *
 * Transform mode (everything else): pinch, twist and two-finger pan at once,
 * about the fingers - the ground under the centroid at the start stays under
 * the centroid while the altitude follows the span and the bearing follows
 * the twist (EarthCameraState.transformAboutScreenPoint). The recognizer
 * engages pinch and twist individually with hysteresis, so a pinch with a
 * few degrees of incidental twist stays a pinch.
 *
 * Targets go through the critically damped spring like every other motion.
 */
export class EarthTwoFingerHandler extends TwoFingerGestureRecognizer {
	private context: EarthBrowserContext
	private cameraState: EarthCameraState
	private startParams?: CameraParams
	private orbitAnchor?: FulcrumOrbitAnchor
	private groundAnchor?: GroundDragAnchor
	private hitAltitudeLimit = false

	constructor(context: EarthBrowserContext, cameraState: EarthCameraState) {
		super({
			slop: 10,
			scaleEngage: 0.03,
			rotationEngage: 2.5 * trig.rad,
			tiltAxisRatio: 1.5
		})
		this.context = context
		this.cameraState = cameraState
		// Above tap (10 / 20) so a two-finger landing on different targets still transforms
		this.priority = 100

		this.onGesture((data: TwoFingerGestureEventData) => {
			switch (data.state) {
				case GestureState.Began:
					this.start(data)
					break
				case GestureState.Changed:
					this.update(data)
					break
				case GestureState.Ended:
				case GestureState.Cancelled:
					this.finish()
					break
			}
		})
	}

	private start(data: TwoFingerGestureEventData): void {
		if (!this.owner) return
		this.context.focusManager.requestFocus(this.owner, { preserveCurrentState: true })
		this.cameraState.syncFromCamera()
		this.startParams = this.cameraState.getCurrentParams()
		if (data.mode === "tilt") {
			this.orbitAnchor = this.cameraState.captureFulcrumAnchor()
			this.groundAnchor = undefined
		} else {
			this.orbitAnchor = undefined
			this.groundAnchor = this.cameraState.captureGroundDragAnchor(data.startX, data.startY)
		}
		this.update(data)
	}

	private update(data: TwoFingerGestureEventData): void {
		if (data.mode === "tilt") {
			this.applyTilt(data)
		} else {
			this.applyTransform(data)
		}
	}

	/** Vertical travel tilts about the fulcrum; horizontal travel pans the tilted view */
	private applyTilt(data: TwoFingerGestureEventData): void {
		const anchor = this.orbitAnchor
		if (!anchor) return

		const tiltDelta = -data.translationY * this.viewportScale() * cCameraTiltSensitivity
		const tilted = this.cameraState.orbitAboutFulcrum(anchor, 0, tiltDelta)
		if (!tilted) return

		let target: CameraParams | undefined = tilted
		if (Math.abs(data.translationX) > 0.5) {
			// The ground under the fingers' start point, as seen from the
			// tilted pose, slides horizontally with them
			const ground = this.cameraState.groundAnchorFor(tilted, data.startX, data.startY)
			if (ground?.hit) {
				target = this.cameraState.dragGroundTo(ground, data.startX + data.translationX, data.startY) ?? tilted
			}
		}
		this.cameraState.animateTo(target, cTiltSpringDamping, "user")
	}

	/** Pinch, twist and pan about the centroid in one solve */
	private applyTransform(data: TwoFingerGestureEventData): void {
		const anchor = this.groundAnchor
		const start = this.startParams
		if (!anchor || !start) return

		const sensitivity = isMobile() ? 1.0 : 0.8
		const altitude = (start.radius - wgs.a) / Math.pow(data.scale, sensitivity)
		const bearing = start.bearing - data.rotation
		const radius = wgs.a + altitude

		// One tick the first time the pinch runs into either end of the
		// altitude range: the spring stops at the fence, so without it the
		// fingers keep moving and nothing answers
		const clamped = this.cameraState.elevationRange.clamp(radius)
		if (clamped !== radius && !this.hitAltitudeLimit) {
			this.hitAltitudeLimit = true
			hapticTick('medium')
		}

		const target = this.cameraState.transformAboutScreenPoint(anchor, data.x, data.y, radius, bearing)
		if (target) this.cameraState.animateTo(target, cTransformSpringDamping, "user")
	}

	private finish(): void {
		this.startParams = undefined
		this.orbitAnchor = undefined
		this.groundAnchor = undefined
		this.hitAltitudeLimit = false
		this.cameraState.saveCameraState()
	}

	/** Drag pixels are worth more in a smaller viewport (fixed vertical FOV) */
	private viewportScale(): number {
		const height = this.cameraState.camera?.projection.height ?? 0
		return height > 0 ? cReferenceViewportHeight / height : 1
	}

	override reset(): void {
		super.reset()
		this.startParams = undefined
		this.orbitAnchor = undefined
		this.groundAnchor = undefined
		this.hitAltitudeLimit = false
	}
}
